// src/components/register/ImageUploader.tsx
import React, { useState, useCallback } from 'react';
import { storage } from '../../config/firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { Upload, X } from 'lucide-react';

interface ImageUploaderProps {
  currentImageUrl?: string;
  onImageUploaded: (url: string) => void;
  maxSizeMB?: number;
  acceptedTypes?: string[];
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  currentImageUrl,
  onImageUploaded,
  maxSizeMB = 5,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'],
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(currentImageUrl);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState<string | null>(null); // アップロードしたファイルのパス

  // ファイルバリデーション
  const validateFile = (file: File): string | null => {
    // ファイルタイプチェック
    if (!acceptedTypes.includes(file.type)) {
      return `ファイル形式が無効です。対応形式: ${acceptedTypes.map((t) => t.split('/')[1]).join(', ')}`;
    }

    // ファイルサイズチェック
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `ファイルサイズは${maxSizeMB}MB以下にしてください（現在: ${(file.size / 1024 / 1024).toFixed(2)}MB）`;
    }

    return null;
  };

  // ファイルアップロード処理
  const uploadFile = async (file: File) => {
    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      // 一時的なIDを生成（実際のagencyIdは登録時に決まるため）
      const tempId = `temp_${Date.now()}`;
      const fileExtension = file.name.split('.').pop();
      const storageRef = ref(storage, `agencies/${tempId}/profile.${fileExtension}`);

      // アップロード開始
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // 進行状況を更新
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          // エラーハンドリング
          console.error('Upload error:', error);
          setError('アップロードに失敗しました。もう一度お試しください。');
          setUploading(false);
        },
        async () => {
          // アップロード完了
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setPreviewUrl(downloadURL);
            setUploadedFilePath(uploadTask.snapshot.ref.fullPath); // ファイルパスを保存
            onImageUploaded(downloadURL);
            setUploading(false);
            setUploadProgress(0);
          } catch (err) {
            console.error('Error getting download URL:', err);
            setError('画像URLの取得に失敗しました。');
            setUploading(false);
          }
        }
      );
    } catch (err) {
      console.error('Upload error:', err);
      setError('アップロードに失敗しました。');
      setUploading(false);
    }
  };

  // ファイル選択ハンドラ
  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      // プレビュー表示
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);

      // アップロード開始
      uploadFile(file);
    },
    [validateFile, uploadFile]
  );

  // ドラッグ&ドロップハンドラ
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // ファイル入力ハンドラ
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // 画像削除ハンドラ
  const handleRemoveImage = async () => {
    // Firebase Storageからファイルを削除
    if (uploadedFilePath) {
      try {
        const fileRef = ref(storage, uploadedFilePath);
        await deleteObject(fileRef);
        console.log('Storage からファイルを削除しました:', uploadedFilePath);
      } catch (err) {
        console.error('Storage からファイルの削除に失敗:', err);
        // エラーが発生してもUIはクリアする（ユーザー体験を優先）
      }
    }

    setPreviewUrl(undefined);
    setUploadedFilePath(null);
    onImageUploaded('');
    setError(null);
  };

  return (
    <div className="space-y-4">
      {/* アップロードエリア */}
      {!previewUrl && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="image-upload"
            className="hidden"
            accept={acceptedTypes.join(',')}
            onChange={handleInputChange}
            disabled={uploading}
          />

          <label
            htmlFor="image-upload"
            className={`cursor-pointer flex flex-col items-center ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Upload className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-sm text-gray-600 mb-2">画像をドラッグ&ドロップ、またはクリックして選択</p>
            <p className="text-xs text-gray-500">対応形式: JPG, PNG, WebP（最大{maxSizeMB}MB）</p>
          </label>
        </div>
      )}

      {/* プレビューエリア */}
      {previewUrl && (
        <div className="relative">
          <div className="relative w-full aspect-square max-w-sm mx-auto bg-gray-100 rounded-lg overflow-hidden">
            <img src={previewUrl} alt="プロフィール画像プレビュー" className="w-full h-full object-cover" />
            {!uploading && (
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* アップロード進行状況 */}
      {uploading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>アップロード中...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* エラーメッセージ */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};
